import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserApiProviderService } from '@core/api';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { addItemToStore } from '../../helpers/database/database';
import { SetActiveWorkspaceId } from '../../services/active/active.actions';
import { SiteRedirectService } from '../auth/site-redirect.service';
import { logErrorInSentry } from '../errors/response-error';
import { ResponseErrorService } from '../errors/response-error.service';
import { TUserResponse } from './response-models/user-response-model';
import { SetUser, UpdateActiveWorkspaceId } from './user.actions';
import { TUser } from './user.model';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class ActiveWorkspaceService {
  private updateActiveWorkspaceIdPromise: Promise<TUserResponse>;
  private switchingWorkspace = false;

  constructor(
    private store: Store<{ user: TUser }>,
    private userApiProviderService: UserApiProviderService,
    private responseErrorService: ResponseErrorService,
    private userService: UserService,
    private siteRedirectService: SiteRedirectService,
  ) {}

  updateActiveWorkspaceId(workspaceId: string): Promise<TUserResponse> {
    this.store.dispatch(new SetActiveWorkspaceId(workspaceId));

    if (this.updateActiveWorkspaceIdPromise) {
      return this.updateActiveWorkspaceIdPromise;
    }

    this.updateActiveWorkspaceIdPromise =
      this.generateUpdateActiveWorkspacePromise(workspaceId).toPromise();

    return this.updateActiveWorkspaceIdPromise;
  }

  private generateUpdateActiveWorkspacePromise(workspaceId: string): Observable<TUserResponse> {
    return this.generateUpdateActiveWorkspacePromiseOnline(workspaceId);
  }

  private generateUpdateActiveWorkspacePromiseOnline(
    workspaceId: string,
  ): Observable<TUserResponse> {
    if (this.switchingWorkspace || !workspaceId) {
      return of(null);
    }

    this.switchingWorkspace = true;

    return this.userApiProviderService.updateActiveWorkspaceId(workspaceId).pipe(
      tap((response: TUserResponse) => {
        this.updateActiveWorkspaceIdPromise = null;

        this.store.dispatch(
          new UpdateActiveWorkspaceId({
            activeWorkspaceId: workspaceId,
          }),
        );

        const newUser = this.userService.generateUser(response);

        this.store.dispatch(new SetUser(newUser));
        addItemToStore('main', 'base', response);
      }),
      catchError((error) => {
        this.updateActiveWorkspaceIdPromise = null;

        this.responseErrorService.errors(error.status);
        logErrorInSentry({ error, message: 'Failed to update active workspace ID' });

        this.checkError(error);

        return throwError(error);
      }),
      finalize(() => {
        this.switchingWorkspace = false;
      }),
    );
  }

  private checkError(error: HttpErrorResponse): void {
    if (error.status === EStatusCode.BAD_REQUEST) {
      this.siteRedirectService.redirectToSites();
    }
  }
}
